<script setup lang="ts">
/**
 * SceneBoxes · 忙碌小剧场（变体）：搬砖
 * ------------------------------------------------------------
 * 舞台 = 一段地面 + 右下角一摞纸箱：小家伙抱着箱子来回搬，
 * 箱堆最上面那只偶尔晃一下（像快倒了又没倒）。
 * 人物比例与所有场景统一：头 r6.6、0.8px/viewBox 单位（见 scenes/index.ts）。
 */
defineProps<{ peek?: boolean; hot?: boolean }>()
</script>

<template>
  <span class="sc" :class="{ hot }" aria-hidden="true">
    <i class="floor" />
    <!-- 箱堆（独立定位，不随小人转身翻转） -->
    <svg class="pile" viewBox="0 0 13 11">
      <rect x=".3" y="6.2" width="6" height="4.4" rx=".8" fill="#D9A96E" stroke="#B4794B" stroke-width=".6" />
      <path d="M3.3 6.2 V10.6" stroke="#B4794B" stroke-width=".6" />
      <rect x="6.9" y="6.6" width="5.6" height="4" rx=".8" fill="#CE9C5F" stroke="#B4794B" stroke-width=".6" />
      <path d="M9.7 6.6 V10.6" stroke="#B4794B" stroke-width=".6" />
      <g>
        <animateTransform attributeName="transform" type="rotate"
                          values="0 6.2 6.2;0 6.2 6.2;-4 6.2 6.2;3 6.2 6.2;0 6.2 6.2;0 6.2 6.2"
                          keyTimes="0;.68;.76;.84;.92;1" dur="5s" repeatCount="indefinite" />
        <rect x="3.4" y="1.6" width="5.6" height="4.6" rx=".8" fill="#D9A96E" stroke="#B4794B" stroke-width=".6" />
        <path d="M6.2 1.6 V6.2" stroke="#B4794B" stroke-width=".6" />
      </g>
    </svg>
    <!-- 抱箱子来回走的小人 -->
    <span class="walk">
      <span class="waddle">
        <svg viewBox="0 0 20 34">
          <defs>
            <linearGradient id="apShirtBox" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stop-color="#7A95FF" />
              <stop offset="1" stop-color="#4C6BF0" />
            </linearGradient>
          </defs>
          <!-- 腿（前后交替摆） + 鞋 -->
          <g>
            <animateTransform attributeName="transform" type="rotate"
                              values="-24 8.6 24.6;24 8.6 24.6;-24 8.6 24.6" dur=".52s" repeatCount="indefinite" />
            <rect x="7.75" y="24" width="1.7" height="6" rx=".85" fill="#3D55C8" />
            <rect x="7.6" y="29.4" width="2.5" height="1.7" rx=".8" fill="#2B2118" />
          </g>
          <g>
            <animateTransform attributeName="transform" type="rotate"
                              values="24 11.4 24.6;-24 11.4 24.6;24 11.4 24.6" dur=".52s" repeatCount="indefinite" />
            <rect x="10.55" y="24" width="1.7" height="6" rx=".85" fill="#3D55C8" />
            <rect x="10.4" y="29.4" width="2.5" height="1.7" rx=".8" fill="#2B2118" />
          </g>
          <!-- 身体（连帽衫） -->
          <rect x="4.4" y="14.6" width="11.2" height="10.4" rx="3.5" fill="url(#apShirtBox)" />
          <path d="M7.3 15.6q2.7 2 5.4 0" stroke="#FFFFFF" stroke-opacity=".5" stroke-width=".7" fill="none" stroke-linecap="round" />
          <!-- 怀里抱着的纸箱 + 两侧扶箱的手 -->
          <path d="M6.8 16.6 L5.9 19.2" stroke="#4C6BF0" stroke-width="1.6" stroke-linecap="round" />
          <path d="M13.2 16.6 L14.1 19.2" stroke="#4C6BF0" stroke-width="1.6" stroke-linecap="round" />
          <rect x="6" y="17" width="8" height="5" rx=".8" fill="#D9A96E" stroke="#B4794B" stroke-width=".6" />
          <path d="M10 17 V22" stroke="#B4794B" stroke-width=".7" />
          <path d="M6 19.4 H14" stroke="#B4794B" stroke-width=".5" opacity=".6" />
          <circle cx="5.7" cy="19.6" r="1.05" fill="#FFDCB4" stroke="#E6B183" stroke-width=".5" />
          <circle cx="14.3" cy="19.6" r="1.05" fill="#FFDCB4" stroke="#E6B183" stroke-width=".5" />
          <!-- 头（正面，会眨） -->
          <g>
            <circle cx="10" cy="8.4" r="6.6" fill="#FFDCB4" stroke="#E6B183" stroke-width=".9" />
            <path d="M3.5 7.9a6.5 6.5 0 0 1 13 0v.8H3.5z" fill="#3A2B21" />
            <path d="M9.5 2.6c1.1-1.6 2.7-1.9 3.5-1.4-1.3.4-2.1 1-2.5 1.8z" fill="#3A2B21" />
            <path d="M6.9 5.9q1.1-.7 2.2-.2M13.1 5.9q-1.1-.7-2.2-.2" stroke="#3A2B21" stroke-width=".7"
                  fill="none" stroke-linecap="round" opacity=".75" />
            <ellipse cx="7.9" cy="8.6" rx="1.5" ry="1.75" fill="#2B2118">
              <animate attributeName="ry" values="1.75;1.75;.3;1.75;1.75" keyTimes="0;.4;.46;.52;1"
                       dur="4.2s" repeatCount="indefinite" />
            </ellipse>
            <ellipse cx="12.1" cy="8.6" rx="1.5" ry="1.75" fill="#2B2118">
              <animate attributeName="ry" values="1.75;1.75;.3;1.75;1.75" keyTimes="0;.4;.46;.52;1"
                       dur="4.2s" repeatCount="indefinite" />
            </ellipse>
            <circle cx="8.5" cy="7.9" r=".5" fill="#FFFFFF" opacity=".9" />
            <circle cx="12.7" cy="7.9" r=".5" fill="#FFFFFF" opacity=".9" />
            <ellipse cx="5.5" cy="11" rx="1.35" ry=".95" fill="#FF9EB5" opacity=".6" />
            <ellipse cx="14.5" cy="11" rx="1.35" ry=".95" fill="#FF9EB5" opacity=".6" />
            <path d="M8.6 11.6a1.5 1.5 0 0 0 2.8 0z" fill="#C05A5A" />
          </g>
        </svg>
      </span>
    </span>
  </span>
</template>

<style scoped>
.sc { position: absolute; inset: 0; display: block; font-size: 0; }

/* 地面光斑 */
.floor {
  position: absolute; left: 50%; bottom: 2px;
  width: 40px; height: 6px; margin-left: -20px; border-radius: 50%;
  background: radial-gradient(closest-side, #1a3a6626, transparent);
}

/* 箱堆：钉在右下角 */
.pile {
  position: absolute; right: 2px; bottom: 2px; z-index: 1;
  width: 11px; height: 9.3px;
  filter: drop-shadow(0 1px 1px #1a3a6626);
}

/* 走路：与 SceneAway 同一套节奏；0.8px/viewBox 单位（20×34 → 16×27.2）；
   抬高 2px：高个站姿与窗台小人保持同一条头部水平线 */
.walk {
  position: absolute; left: 50%; bottom: 2px; z-index: 2;
  width: 16px; margin-left: -14px;
  transform-origin: 50% 100%;
  filter: drop-shadow(0 1px 1px #1a3a6626);
  animation: bxWalk 8s ease-in-out infinite;
}
.walk svg { display: block; width: 16px; height: 27.2px; }
.sc.hot .walk { animation-duration: 5.2s; }
@keyframes bxWalk {
  0%   { transform: translateX(0) scaleX(1); }
  40%  { transform: translateX(12px) scaleX(1); }
  44%  { transform: translateX(12px) scaleX(.35); }
  48%  { transform: translateX(12px) scaleX(-1); }
  88%  { transform: translateX(0) scaleX(-1); }
  92%  { transform: translateX(0) scaleX(.35); }
  96%  { transform: translateX(0) scaleX(1); }
  100% { transform: translateX(0) scaleX(1); }
}
.waddle { display: block; animation: bxWaddle .5s ease-in-out infinite alternate; }
@keyframes bxWaddle {
  from { transform: rotate(-3.5deg) translateY(0); }
  to   { transform: rotate(4deg) translateY(-.6px); }
}
</style>
